// src/pages/StrategyPage.js
import React, { useEffect, useMemo, useState } from 'react';
import { useAppContext } from '../context/AppContext';
import {
  fetchStrategy,
  getCachedStrategy,
  inferColumnTypesFromRows,
  computeFingerprint,
  clearStrategyCache,
} from '../utils/api';

import '../styles/StrategyPage.css';
import StrategyPanel from '../components/StrategyPanel';
// --- Strategy DEBUG controls ---
const DEBUG_MODE =
  new URLSearchParams(window.location.search).get('debug') === '1' ||
  localStorage.getItem('clairifi_debug') === '1';

const logDebug = (...args) => { if (DEBUG_MODE) console.debug('[StrategyDebug]', ...args); };


const IS_MOBILE = typeof window !== 'undefined'
  && window.matchMedia
  && window.matchMedia('(max-width: 480px)').matches;

function clamp(s = '', n = 300) {
  const t = String(s || '').trim();
  if (IS_MOBILE) return t;            // no truncation on phones
  return t.length <= n ? t : t.slice(0, n - 1).trimEnd() + '…';
}


export default function StrategyPage() {
  const {
    user,
    token,
    csvData,
    columnTypes,
    companyName,
    strategyData,
    setStrategyData,
  } = useAppContext();

  const rowsReady = Array.isArray(csvData) && csvData.length > 0 && csvData[0] && Object.keys(csvData[0]).length > 0;

  const fp = useMemo(() => {
    if (!rowsReady) return null;
    const ct = (columnTypes && Object.keys(columnTypes).length > 0)
      ? columnTypes
      : inferColumnTypesFromRows(csvData);
    return computeFingerprint({ rows: csvData, columnTypes: ct || {}, companyName: companyName || '' });
  }, [rowsReady, csvData, columnTypes, companyName]);

  const initial = useMemo(() => {
    if (!user?.id || !fp) return null;
    return getCachedStrategy(user.id, fp);
  }, [user?.id, fp]);

  const [data, setData]   = useState(initial || strategyData || null);
  const [didAutoRefresh, setDidAutoRefresh] = useState(false);
  const [loading, setLoading] = useState(!initial && rowsReady);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!rowsReady) {
        if (DEBUG_MODE) logDebug('Skipping fetch: rowsReady=false');
        setData(null);
        setLoading(false);
        return;
      }
      if (DEBUG_MODE) logDebug('Effect start: rowsReady=true user.id=', user?.id, 'fp=', fp);

      setLoading(true);
      setError(null);

      const ct = (columnTypes && Object.keys(columnTypes).length > 0)
        ? columnTypes
        : inferColumnTypesFromRows(csvData);

      try {
        if (DEBUG_MODE) {
          logDebug('Calling fetchStrategy with fingerprint=', fp, 'ct keys=', Object.keys(ct||{}), 'rowsReady=', rowsReady);
          try { if (user?.id && fp) clearStrategyCache(user.id, fp); } catch {}
        }

        const payload = {
          column_types: ct || {},
          company_description: companyName || '',
          raw_csv_base64: rowsReady ? btoa(unescape(encodeURIComponent(
            [Object.keys(csvData[0]).join(',')]
              .concat(csvData.map(r => Object.values(r).map(v => String(v).replace(/,/g, '')).join(',')))
              .join('\n')
          ))) : undefined,
        };

        let res = await fetchStrategy(payload, { token });

        // one-shot auto-refresh if the payload is empty (avoids manual "wipe cache")
        const viz0 = res?.visualization;
        const looksEmpty =
          !res ||
          (
            (!viz0) ||
            (
              (!Array.isArray(viz0.actions) || viz0.actions.length === 0) &&
              (!Array.isArray(viz0.risks)   || viz0.risks.length   === 0) &&
              (!Array.isArray(viz0.recs)    || viz0.recs.length    === 0) &&
              (!Array.isArray(viz0.ops)     || viz0.ops.length     === 0)
            )
          );

        if (looksEmpty && !didAutoRefresh) {
          setDidAutoRefresh(true);
          try {
            res = await fetchStrategy(payload, { token, refresh: 1 });
          } catch { /* keep original res */ }
        }

        if (!mounted) return;
        setData(res || null);
        setError(null);
        setStrategyData?.(res || null);

      } catch (e) {
        if (!mounted) return;
        setError(e?.message || 'Failed to generate strategy.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fp, rowsReady, token, user?.id]);

  const viz = data?.visualization || {};
  const executiveSummary = viz?.executiveSummary || data?.trend_summary || '';
  const responseKeys = data ? Object.keys(data) : [];
  const hasOnlySummary =
    !!executiveSummary &&
    (!viz || (
      (!Array.isArray(viz.actions) || viz.actions.length === 0) &&
      (!Array.isArray(viz.risks)   || viz.risks.length   === 0) &&
      (!Array.isArray(viz.recs)    || viz.recs.length    === 0) &&
      (!Array.isArray(viz.ops)     || viz.ops.length     === 0)
    ));


  if (DEBUG_MODE) {
    logDebug('Exec summary length:', (executiveSummary || '').length);
    logDebug('Response keys:', responseKeys);
    logDebug('Visualization keys:', viz ? Object.keys(viz) : []);
    logDebug('hasOnlySummary:', hasOnlySummary);
  }

  return (
    <div className="strategy-page">
      <div className="container">
        <header className="str-header">
          <h1 className="strategy-title">Strategic Plan</h1>
          <div className="sp-actions">
            <button
              className="sp-regenerate"
              disabled={loading}
              onClick={async () => {
                try {
                  setLoading(true);
                  setError(null);
                  try { if (user?.id && fp) await clearStrategyCache(user.id, fp); } catch {}

                  const ct = (columnTypes && Object.keys(columnTypes).length > 0)
                    ? columnTypes
                    : inferColumnTypesFromRows(csvData);

                  const payload = {
                    column_types: ct || {},
                    company_description: companyName || '',
                    raw_csv_base64: rowsReady ? btoa(unescape(encodeURIComponent(
                      [Object.keys(csvData[0]).join(',')]
                        .concat(csvData.map(r => Object.values(r).map(v => String(v).replace(/,/g, '')).join(',')))
                        .join('\n')
                    ))) : undefined,
                  };

                  const fresh = await fetchStrategy(payload, { token, refresh: 1 });
                  setData(fresh);
                  setStrategyData?.(fresh);
                } catch (e) {
                  setError(e?.message || 'Failed to regenerate.');
                } finally {
                  setLoading(false);
                }
              }}

            >
              Regenerate Plan
            </button>
          </div>

          <p className="strategy-subtext">Opportunities, risks, and execution priorities.</p>
        </header>

        <main className="str-fullwidth">
        {DEBUG_MODE && (
          <div style={{
            position:'fixed', right:16, bottom:16, zIndex:9999,
            background:'#111', color:'#fff', padding:'12px 14px',
            borderRadius:8, maxWidth:460, boxShadow:'0 4px 16px rgba(0,0,0,0.3)'
          }}>
            <div style={{fontWeight:700, marginBottom:6}}>Strategy DEBUG</div>
            <div style={{fontSize:12, lineHeight:'16px'}}>
              <div><b>Keys:</b> {responseKeys.join(', ') || '(none)'}</div>
              <div><b>Has only summary?</b> {String(hasOnlySummary)}</div>
              <div><b>Viz keys:</b> {viz ? Object.keys(viz).join(', ') : '(none)'}</div>
              <div><b>Auth token present?</b> {token ? 'yes' : 'no'}</div>
              <div style={{marginTop:8, display:'flex', gap:8, flexWrap:'wrap'}}>
                <button
                  onClick={() => {
                    try { if (user?.id && fp) clearStrategyCache(user.id, fp); } catch {}
                    window.location.reload();
                  }}
                  style={{padding:'6px 10px', borderRadius:6, border:'1px solid #444', background:'#222', color:'#fff', cursor:'pointer'}}
                >
                  Wipe cache & refetch
                </button>
              </div>
            </div>
          </div>
        )}
          {(!loading && executiveSummary) ? (
            <section className="card str-exec-brief">
              <div className="row-inner">
                <div className="exec-brief-title">Executive Summary</div>
                <p className="exec-brief-text">{clamp(executiveSummary, 480)}</p>

                {hasOnlySummary && (
                  <div style={{marginTop:12, padding:'10px 12px',
                    background:'#fff3cd', border:'1px solid #ffeeba', borderRadius:8}}>
                    API returned only the summary. Add <code>?debug=1</code> to the URL to inspect why.
                  </div>
                )}
              </div>
            </section>
          ) : null}


          <StrategyPanel
            loading={loading}
            error={error}
            data={data || {}}
          />
        </main>
      </div>
    </div>
  );
}
